<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <b-card header="Alternate Account Lookup" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="text-muted mb-1" style="font-size:0.85rem;">* — denotes a required field</p>
        <p class="mb-3" style="font-size:0.9rem;">
          Enter the following account details to verify your identity. Once verified, you will be able to reset your password and update your email
          address.
        </p>

        <b-form @submit.prevent="onSubmit">
          <b-form-group label="UBC ID: *" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ubcid" v-mask="'U-####-####'" :state="validated('ubcid')" placeholder="e.g. U-1234-5678" trim @paste="onUbcIdPaste" />
            <b-form-invalid-feedback>A valid UBC ID is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Last Name: *" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.lastname" :state="validated('lastname')" placeholder="Last Name" trim />
            <b-form-invalid-feedback>Last Name is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Birth Date: *" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.birthdate" v-mask="'##/##/####'" :state="validated('birthdate')" placeholder="MM/DD/YYYY" trim />
            <b-form-invalid-feedback v-if="!form.birthdate">Birth date is required</b-form-invalid-feedback>
            <b-form-invalid-feedback v-else>Invalid date</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="SSN/SIN (Last 4 digits): *" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ssn" v-mask="'####'" :state="validated('ssn')" placeholder="Last 4 digits" trim />
            <b-form-invalid-feedback v-if="!form.ssn">SSN/SIN is required</b-form-invalid-feedback>
            <b-form-invalid-feedback v-else>Enter the last 4 digits only</b-form-invalid-feedback>
          </b-form-group>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'request-username' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Look Up Account
            </b-button>
          </div>
        </b-form>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { isValid as isValidUbcId } from '@/util/ubc_id';
import { isValidDobString } from '@/util/date';
import { renderErrorBody } from '@/util/error';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import recaptchaMixin from '@/mixins/recaptcha_fallback';

@Component({
  metaInfo: {
    title: 'Member Portal - Alternate Account Lookup'
  },
  components: { RecaptchaV2Modal }
})
export default class AlternateAccountLookup extends Mixins(recaptchaMixin) {
  form = {
    ubcid: '',
    lastname: '',
    birthdate: '',
    ssn: ''
  };

  loading = false;
  submitted = false;

  get submitDisabled() {
    return ['ubcid', 'lastname', 'birthdate', 'ssn'].some(k => this.validated(k) !== true);
  }

  validated(field) {
    if (!this.submitted && !this.form[field]) {
      return undefined;
    }
    const value = (this.form[field] || '').trim();
    switch (field) {
      case 'ubcid':
        return isValidUbcId(value);
      case 'lastname':
        return value.length > 0;
      case 'birthdate':
        return isValidDobString(value);
      case 'ssn':
        return value.replace(/\D/g, '').length === 4;
      default:
        return true;
    }
  }

  onUbcIdPaste(e) {
    e.preventDefault();
    const raw = (e.clipboardData || window.clipboardData).getData('text');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let val = 'U';
    if (digits.length > 0) val += '-' + digits.slice(0, 4);
    if (digits.length > 4) val += '-' + digits.slice(4);
    this.form.ubcid = val;
  }

  mounted() {
    this.loadRecaptcha();
  }

  async onSubmit() {
    try {
      this.submitted = true;
      if (this.submitDisabled) {
        return this.$bvToast.toast('Please correct the highlighted errors.', {
          title: 'Error',
          solid: true,
          variant: 'danger'
        });
      }
      this.loading = true;
      const payload = {
        ubcid: this.form.ubcid.trim(),
        lastname: this.form.lastname.trim()
      };
      if (this.form.birthdate) {
        // Convert MM/DD/YYYY → YYYY-MM-DD for the API
        const parts = this.form.birthdate.trim().split('/');
        payload.birthdate = parts.length === 3 ? `${parts[2]}-${parts[0]}-${parts[1]}` : this.form.birthdate.trim();
      }
      if (this.form.ssn) payload.ssn = this.form.ssn.replace(/\D/g, '');
      const result = await this.submitWithRecaptcha('account_lookup', tokens =>
        memberDao.alternateAccountLookup({ ...payload, ...tokens })
      );
      this.$router.push({ name: 'complete-recovery', query: { token: result.token } });
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Account Lookup Failed',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
